import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Typography,
} from "@mui/material"
import { alpha, useTheme } from "@mui/material/styles"
import { useTranslation } from "react-i18next"
import {
    getMyAssignments,
    getOutletRegisters,
} from "../services/assignmentService"
import { getCachedCashierFlow } from "../services/authService"
import { useAuth } from "../services/AuthContext"
import cashSessionService from "../services/cashSessionService"
import CashFundCountDialog from "../components/CashFundCountDialog"
import { formatCentsWithSymbol } from "../utils/formatCurrency"

type RegisterOption = {
    registerId: string
    registerName: string
    outletId: string
    outletName: string
    companyId: string
    isBusy: boolean
}

type OutletOption = {
    outletId: string
    outletName: string
    companyId: string
}

function normalizeFromCachedFlow(
    flow: any,
    fallbackRegisterName: string,
    fallbackOutletName: string
): RegisterOption[] {
    const rows = Array.isArray(flow?.assignedRegisters)
        ? flow.assignedRegisters
        : []
    return rows
        .filter(
            (row: any) => row?.registerId && row?.outletId && row?.companyId
        )
        .map((row: any) => ({
            registerId: row.registerId,
            registerName: row.registerName || fallbackRegisterName,
            outletId: row.outletId,
            outletName: row.outletName || fallbackOutletName,
            companyId: row.companyId,
            isBusy: false,
        }))
}

function resolveOpeningReferenceMinor(session: any): number {
    if (!session) {
        return 0
    }

    if (session.counted_cash_minor != null) {
        return Number(session.counted_cash_minor)
    }

    if (session.expected_cash_minor != null) {
        return Number(session.expected_cash_minor)
    }

    return Number(session.opening_float_minor || 0)
}

function resolveChequeReference(report: any): {
    amountMinor: number
    count: number
} {
    const methods = Array.isArray(report?.paymentMethods)
        ? report.paymentMethods
        : []

    return methods.reduce(
        (acc: { amountMinor: number; count: number }, row: any) => {
            if (String(row?.type || "").toLowerCase() !== "cheque") {
                return acc
            }

            acc.amountMinor += Number(
                row?.total_amount_minor ?? row?.totalAmountMinor ?? 0
            )
            acc.count += Number(
                row?.transaction_count ?? row?.transactionCount ?? 0
            )
            return acc
        },
        { amountMinor: 0, count: 0 }
    )
}

function CashRegisterSelectionPage() {
    const navigate = useNavigate()
    const theme = useTheme()
    const { t, i18n } = useTranslation()
    const { scope } = useAuth() as any
    const locale = i18n.language || "fr"
    const registerFallbackLabel = t("cashRegisterSelection.registerFallback")
    const outletFallbackLabel = t("cashRegisterSelection.outletFallback")
    const loadAssignmentsError = t("cashRegisterSelection.errors.assignments")
    const loadRegistersError = t("cashRegisterSelection.errors.registers")
    const openSessionError = t("cashRegisterSelection.errors.openSession")
    const timeoutSessionError = t("cashRegisterSelection.errors.timeoutSession")
    const timeoutSessionNotFoundError = t(
        "cashRegisterSelection.errors.timeoutSessionNotFound"
    )
    const noAssignmentsWarning = t("cashRegisterSelection.alerts.noAssignments")
    const noActiveRegistersInfo = t(
        "cashRegisterSelection.alerts.noActiveRegisters"
    )
    const allBusyWarning = t("cashRegisterSelection.alerts.allBusy")
    const registerBusySuffix = t(
        "cashRegisterSelection.registerSection.busySuffix"
    )
    const previousFloatLoadingLabel = t(
        "cashRegisterSelection.previousFloat.loading"
    )

    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [outletOptions, setOutletOptions] = useState<OutletOption[]>([])
    const [selectedOutletId, setSelectedOutletId] = useState("")
    const [allowedRegisterIdsByOutlet, setAllowedRegisterIdsByOutlet] =
        useState<Record<string, string[]>>({})
    const [registerOptions, setRegisterOptions] = useState<RegisterOption[]>([])
    const [registersLoading, setRegistersLoading] = useState(false)
    const [selectedRegisterId, setSelectedRegisterId] = useState("")
    const [openingReferenceMinor, setOpeningReferenceMinor] = useState(0)
    const [chequeOpeningReferenceMinor, setChequeOpeningReferenceMinor] =
        useState(0)
    const [chequeOpeningReferenceCount, setChequeOpeningReferenceCount] =
        useState(0)
    const [openingReferenceLoading, setOpeningReferenceLoading] =
        useState(false)
    const [showCountDialog, setShowCountDialog] = useState(false)
    const [showTimeoutDialog, setShowTimeoutDialog] = useState(false)
    const [timeoutDialogRegister, setTimeoutDialogRegister] =
        useState<RegisterOption | null>(null)
    const [timingOutRegisterId, setTimingOutRegisterId] = useState<
        string | null
    >(null)

    const selectedOutlet = useMemo(
        () =>
            outletOptions.find(
                (outlet) => outlet.outletId === selectedOutletId
            ) || null,
        [outletOptions, selectedOutletId]
    )

    const selectedRegister = useMemo(
        () =>
            registerOptions.find(
                (register) => register.registerId === selectedRegisterId
            ) || null,
        [registerOptions, selectedRegisterId]
    )

    const selectableRegisterCount = useMemo(
        () => registerOptions.filter((register) => !register.isBusy).length,
        [registerOptions]
    )

    const outletLocked = outletOptions.length <= 1
    const canForceTimeout = scope?.canAccessAdmin === true
    const canStartSession =
        !!selectedOutlet &&
        !!selectedRegister &&
        !selectedRegister.isBusy &&
        !submitting &&
        !loading
    const canTimeoutSelectedRegister =
        canForceTimeout &&
        !!selectedRegister &&
        selectedRegister.isBusy &&
        !submitting &&
        !loading &&
        !registersLoading
    const canConfirmTimeoutDialog =
        canForceTimeout &&
        !!timeoutDialogRegister &&
        timeoutDialogRegister.isBusy &&
        !submitting &&
        !loading &&
        !registersLoading

    useEffect(() => {
        let cancelled = false

        async function loadBootstrap() {
            setLoading(true)
            setError(null)

            try {
                const cachedOptions = normalizeFromCachedFlow(
                    getCachedCashierFlow(),
                    registerFallbackLabel,
                    outletFallbackLabel
                )

                if (cachedOptions.length > 0) {
                    const outletMap: Record<string, OutletOption> = {}
                    const allowedMap: Record<string, string[]> = {}

                    cachedOptions.forEach((row) => {
                        outletMap[row.outletId] = {
                            outletId: row.outletId,
                            outletName: row.outletName,
                            companyId: row.companyId,
                        }

                        if (!Array.isArray(allowedMap[row.outletId])) {
                            allowedMap[row.outletId] = []
                        }
                        if (
                            !allowedMap[row.outletId].includes(row.registerId)
                        ) {
                            allowedMap[row.outletId].push(row.registerId)
                        }
                    })

                    const outlets = Object.values(outletMap).sort((a, b) =>
                        a.outletName.localeCompare(b.outletName, locale)
                    )

                    if (!cancelled) {
                        setOutletOptions(outlets)
                        setAllowedRegisterIdsByOutlet(allowedMap)
                        setSelectedOutletId(outlets[0]?.outletId || "")
                        setSelectedRegisterId("")
                    }
                    return
                }

                const assignments = await getMyAssignments()
                const isCashier = assignments.isCashier === true
                const outletAssignments = assignments.outletAssignments || []
                const cashRegisterAssignments =
                    assignments.cashRegisterAssignments || []
                const hasScopedRegisterAssignments =
                    isCashier && cashRegisterAssignments.length > 0
                const outletMap: Record<string, OutletOption> = {}
                const allowedMap: Record<string, string[]> = {}

                outletAssignments.forEach((assignment: any) => {
                    if (!assignment?.outlet_id || !assignment?.company_id) {
                        return
                    }
                    outletMap[assignment.outlet_id] = {
                        outletId: assignment.outlet_id,
                        outletName: assignment.outlet_name || "Point de vente",
                        companyId: assignment.company_id,
                    }

                    if (
                        hasScopedRegisterAssignments &&
                        !Array.isArray(allowedMap[assignment.outlet_id])
                    ) {
                        allowedMap[assignment.outlet_id] = []
                    }
                })

                cashRegisterAssignments.forEach((assignment: any) => {
                    if (
                        !assignment?.outlet_id ||
                        !assignment?.cash_register_id
                    ) {
                        return
                    }
                    if (!Array.isArray(allowedMap[assignment.outlet_id])) {
                        allowedMap[assignment.outlet_id] = []
                    }
                    if (
                        !allowedMap[assignment.outlet_id].includes(
                            assignment.cash_register_id
                        )
                    ) {
                        allowedMap[assignment.outlet_id].push(
                            assignment.cash_register_id
                        )
                    }
                })

                const outlets = Object.values(outletMap).sort((a, b) =>
                    a.outletName.localeCompare(b.outletName, locale)
                )

                if (!cancelled) {
                    setOutletOptions(outlets)
                    setAllowedRegisterIdsByOutlet(allowedMap)
                    setSelectedOutletId(outlets[0]?.outletId || "")
                    setSelectedRegisterId("")
                }
            } catch (e: any) {
                if (!cancelled) {
                    setError(e?.message || loadAssignmentsError)
                }
            } finally {
                if (!cancelled) {
                    setLoading(false)
                }
            }
        }

        loadBootstrap()
        return () => {
            cancelled = true
        }
    }, [
        registerFallbackLabel,
        outletFallbackLabel,
        loadAssignmentsError,
        locale,
    ])

    useEffect(() => {
        let cancelled = false

        async function loadRegistersForOutlet() {
            if (!selectedOutletId) {
                setRegisterOptions([])
                setSelectedRegisterId("")
                return
            }

            const outlet = outletOptions.find(
                (row) => row.outletId === selectedOutletId
            )
            if (!outlet) {
                setRegisterOptions([])
                setSelectedRegisterId("")
                return
            }

            setRegistersLoading(true)
            setError(null)

            try {
                const [rawRegisters, posContext] = await Promise.all([
                    getOutletRegisters(selectedOutletId),
                    cashSessionService
                        .getPosContext(outlet.companyId)
                        .catch(() => null),
                ])
                if (cancelled) {
                    return
                }

                const activeSession = (posContext as any)?.activeSession
                if (
                    activeSession?.company_id &&
                    activeSession?.outlet_id &&
                    activeSession?.register_id
                ) {
                    navigate("/pos", {
                        replace: true,
                        state: {
                            selectedCompanyId: activeSession.company_id,
                            selectedOutletId: activeSession.outlet_id,
                            selectedRegisterId: activeSession.register_id,
                        },
                    })
                    return
                }

                const contextRegisters = Array.isArray(posContext?.registers)
                    ? posContext!.registers
                    : []
                const busyRegisterIds = new Set(
                    contextRegisters
                        .filter(
                            (row: any) =>
                                (row?.outlet_id || row?.outletId) ===
                                selectedOutletId
                        )
                        .filter(
                            (row: any) =>
                                String(row?.status || "").toLowerCase() ===
                                "used"
                        )
                        .map((row: any) => row.id)
                )

                const allowedList =
                    allowedRegisterIdsByOutlet[selectedOutletId] || []
                const hasAllowedRestrictions =
                    Object.keys(allowedRegisterIdsByOutlet).length > 0
                const allowedSet = hasAllowedRestrictions
                    ? new Set(allowedList)
                    : null

                const normalized: RegisterOption[] = (rawRegisters || [])
                    .filter((row: any) => row?.id && row.is_active !== false)
                    .filter((row: any) => !allowedSet || allowedSet.has(row.id))
                    .map((row: any) => ({
                        registerId: row.id,
                        registerName: row.name || "Caisse",
                        outletId: selectedOutletId,
                        outletName: outlet.outletName,
                        companyId: outlet.companyId,
                        isBusy: busyRegisterIds.has(row.id),
                    }))

                setRegisterOptions(normalized)
                setSelectedRegisterId("")
            } catch (e: any) {
                if (!cancelled) {
                    setRegisterOptions([])
                    setSelectedRegisterId("")
                    setError(e?.message || loadRegistersError)
                }
            } finally {
                if (!cancelled) {
                    setRegistersLoading(false)
                }
            }
        }

        loadRegistersForOutlet()

        return () => {
            cancelled = true
        }
    }, [
        selectedOutletId,
        outletOptions,
        allowedRegisterIdsByOutlet,
        loadRegistersError,
    ])

    useEffect(() => {
        let cancelled = false

        async function loadOpeningReference() {
            if (!selectedOutlet || !selectedRegisterId) {
                setOpeningReferenceMinor(0)
                setChequeOpeningReferenceMinor(0)
                setChequeOpeningReferenceCount(0)
                setOpeningReferenceLoading(false)
                return
            }

            setOpeningReferenceLoading(true)
            try {
                const latestClosedSession =
                    await cashSessionService.getLatestClosedSessionByRegister(
                        selectedOutlet.companyId,
                        selectedRegisterId
                    )

                const cashReferenceMinor =
                    resolveOpeningReferenceMinor(latestClosedSession)
                let chequeReferenceMinor = 0
                let chequeReferenceCount = 0

                if (latestClosedSession?.id) {
                    try {
                        const zReport = await cashSessionService.getZReport(
                            selectedOutlet.companyId,
                            latestClosedSession.id
                        )
                        const chequeReference = resolveChequeReference(zReport)
                        chequeReferenceMinor = chequeReference.amountMinor
                        chequeReferenceCount = chequeReference.count
                    } catch (_reportError) {
                        chequeReferenceMinor = 0
                        chequeReferenceCount = 0
                    }
                }

                if (!cancelled) {
                    setOpeningReferenceMinor(cashReferenceMinor)
                    setChequeOpeningReferenceMinor(chequeReferenceMinor)
                    setChequeOpeningReferenceCount(chequeReferenceCount)
                }
            } catch (_e) {
                if (!cancelled) {
                    setOpeningReferenceMinor(0)
                    setChequeOpeningReferenceMinor(0)
                    setChequeOpeningReferenceCount(0)
                }
            } finally {
                if (!cancelled) {
                    setOpeningReferenceLoading(false)
                }
            }
        }

        loadOpeningReference()
        return () => {
            cancelled = true
        }
    }, [selectedOutlet, selectedRegisterId])

    const openSessionWithAmount = useCallback(
        async (openingFloatMinor: number) => {
            if (!selectedOutlet || !selectedRegister) {
                return
            }

            setError(null)
            setSubmitting(true)
            try {
                await cashSessionService.openSession(selectedOutlet.companyId, {
                    outletId: selectedOutlet.outletId,
                    registerId: selectedRegister.registerId,
                    openingFloatMinor: Math.max(
                        0,
                        Math.round(Number(openingFloatMinor || 0))
                    ),
                    currency: "EUR",
                })

                navigate("/pos", {
                    replace: true,
                    state: {
                        selectedCompanyId: selectedOutlet.companyId,
                        selectedOutletId: selectedOutlet.outletId,
                        selectedRegisterId: selectedRegister.registerId,
                    },
                })
            } catch (e: any) {
                setError(e?.message || openSessionError)
            } finally {
                setSubmitting(false)
            }
        },
        [navigate, openSessionError, selectedOutlet, selectedRegister]
    )

    const handleOpenWithPreviousFloat = useCallback(async () => {
        if (!canStartSession) {
            return
        }
        await openSessionWithAmount(openingReferenceMinor)
    }, [canStartSession, openSessionWithAmount, openingReferenceMinor])

    const handleOpenCountDialog = useCallback(() => {
        if (!canStartSession) {
            return
        }
        setShowCountDialog(true)
    }, [canStartSession])

    const handleConfirmCount = useCallback(
        async (countedMinor: number) => {
            await openSessionWithAmount(countedMinor)
        },
        [openSessionWithAmount]
    )

    const handleOpenTimeoutDialog = useCallback(() => {
        if (!selectedRegister || !selectedRegister.isBusy || !canForceTimeout) {
            return
        }
        setTimeoutDialogRegister(selectedRegister)
        setShowTimeoutDialog(true)
    }, [canForceTimeout, selectedRegister])

    const handleCloseTimeoutDialog = useCallback(() => {
        if (submitting) {
            return
        }
        setShowTimeoutDialog(false)
        setTimeoutDialogRegister(null)
    }, [submitting])

    const handleTimeoutSelectedRegister = useCallback(async () => {
        if (
            !timeoutDialogRegister ||
            !timeoutDialogRegister.isBusy ||
            !canForceTimeout
        ) {
            return
        }

        setError(null)
        setSubmitting(true)
        setTimingOutRegisterId(timeoutDialogRegister.registerId)
        try {
            const openSession = await cashSessionService.getOpenSession(
                timeoutDialogRegister.companyId,
                timeoutDialogRegister.registerId
            )
            const sessionId = String((openSession as any)?.id || "")
            if (!sessionId) {
                throw new Error(timeoutSessionNotFoundError)
            }

            await cashSessionService.timeoutSession(
                timeoutDialogRegister.companyId,
                sessionId
            )

            setRegisterOptions((prev) =>
                prev.map((row) =>
                    row.registerId === timeoutDialogRegister.registerId
                        ? { ...row, isBusy: false }
                        : row
                )
            )
        } catch (e: any) {
            setError(e?.message || timeoutSessionError)
        } finally {
            setTimingOutRegisterId(null)
            setSubmitting(false)
            setShowTimeoutDialog(false)
            setTimeoutDialogRegister(null)
        }
    }, [
        canForceTimeout,
        timeoutDialogRegister,
        timeoutSessionError,
        timeoutSessionNotFoundError,
    ])

    const previousFundLabel = openingReferenceLoading
        ? previousFloatLoadingLabel
        : formatCentsWithSymbol(openingReferenceMinor, "EUR")

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 5 } }}>
            <Paper
                variant="outlined"
                sx={{
                    p: { xs: 2.25, sm: 3.5 },
                    borderRadius: 3,
                    borderColor: alpha(theme.palette.primary.main, 0.2),
                    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)",
                    maxWidth: 920,
                    mx: "auto",
                }}
            >
                <Typography
                    variant="overline"
                    sx={{
                        color: "primary.main",
                        letterSpacing: 1,
                        fontWeight: 800,
                    }}
                >
                    {t("cashRegisterSelection.overline")}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                    {t("cashRegisterSelection.title")}
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2.5 }}
                >
                    {t("cashRegisterSelection.subtitle")}
                </Typography>

                {error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : null}

                {loading ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 3,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : null}

                {!loading && outletOptions.length === 0 ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {noAssignmentsWarning}
                    </Alert>
                ) : null}

                {!loading && outletOptions.length > 0 ? (
                    <Box sx={{ display: "grid", gap: 1.5 }}>
                        <Box
                            sx={{
                                p: 1.75,
                                border: "1px solid #e5e7eb",
                                borderRadius: 2,
                                backgroundColor: "#fafafa",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    mb: 1,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: "0.85rem",
                                        color: "#6b7280",
                                        fontWeight: 700,
                                    }}
                                >
                                    {t(
                                        "cashRegisterSelection.outletSection.title"
                                    )}
                                </Typography>
                                {outletLocked ? (
                                    <Typography
                                        sx={{
                                            fontSize: "0.75rem",
                                            color: "#6b7280",
                                            fontWeight: 600,
                                        }}
                                    >
                                        {t(
                                            "cashRegisterSelection.outletSection.singleAssignment"
                                        )}
                                    </Typography>
                                ) : null}
                            </Box>

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns:
                                        "repeat(auto-fit, minmax(165px, 1fr))",
                                    gap: 1,
                                }}
                            >
                                {outletOptions.map((outlet) => {
                                    const isSelected =
                                        outlet.outletId === selectedOutletId
                                    return (
                                        <Button
                                            key={outlet.outletId}
                                            variant="outlined"
                                            onClick={() =>
                                                setSelectedOutletId(
                                                    outlet.outletId
                                                )
                                            }
                                            disabled={
                                                submitting ||
                                                registersLoading ||
                                                (outletLocked && isSelected)
                                            }
                                            sx={{
                                                justifyContent: "flex-start",
                                                py: 1.1,
                                                borderRadius: 2,
                                                borderColor: isSelected
                                                    ? theme.palette.primary.main
                                                    : "#d1d5db",
                                                backgroundColor: isSelected
                                                    ? alpha(
                                                          theme.palette.primary
                                                              .main,
                                                          0.08
                                                      )
                                                    : "#ffffff",
                                                color: isSelected
                                                    ? theme.palette.primary.dark
                                                    : "#374151",
                                                fontWeight: isSelected
                                                    ? 700
                                                    : 600,
                                                "&:hover": {
                                                    borderColor:
                                                        theme.palette.primary
                                                            .main,
                                                    backgroundColor: alpha(
                                                        theme.palette.primary
                                                            .main,
                                                        isSelected ? 0.12 : 0.04
                                                    ),
                                                },
                                            }}
                                        >
                                            {outlet.outletName}
                                        </Button>
                                    )
                                })}
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                p: 1.75,
                                border: "1px solid #e5e7eb",
                                borderRadius: 2,
                                backgroundColor: "#ffffff",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "0.85rem",
                                    color: "#6b7280",
                                    mb: 1,
                                    fontWeight: 700,
                                }}
                            >
                                {t(
                                    "cashRegisterSelection.registerSection.title"
                                )}
                            </Typography>

                            {registersLoading ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        py: 2,
                                    }}
                                >
                                    <CircularProgress size={22} />
                                </Box>
                            ) : null}

                            {!registersLoading &&
                            registerOptions.length === 0 ? (
                                <Alert severity="info" sx={{ mb: 0.5 }}>
                                    {noActiveRegistersInfo}
                                </Alert>
                            ) : null}

                            {!registersLoading &&
                            registerOptions.length > 0 &&
                            selectableRegisterCount === 0 ? (
                                <Alert severity="warning" sx={{ mb: 0.5 }}>
                                    {allBusyWarning}
                                </Alert>
                            ) : null}

                            {!registersLoading && registerOptions.length > 0 ? (
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns:
                                            "repeat(auto-fit, minmax(165px, 1fr))",
                                        gap: 1,
                                    }}
                                >
                                    {registerOptions.map((register) => {
                                        const isSelected =
                                            register.registerId ===
                                            selectedRegisterId
                                        const isBusy = register.isBusy
                                        return (
                                            <Button
                                                key={register.registerId}
                                                variant="outlined"
                                                onClick={() =>
                                                    setSelectedRegisterId(
                                                        register.registerId
                                                    )
                                                }
                                                disabled={
                                                    submitting ||
                                                    (isBusy && !canForceTimeout)
                                                }
                                                sx={{
                                                    justifyContent:
                                                        "flex-start",
                                                    py: 1.1,
                                                    borderRadius: 2,
                                                    borderColor: isSelected
                                                        ? theme.palette.primary
                                                              .main
                                                        : "#d1d5db",
                                                    backgroundColor: isSelected
                                                        ? alpha(
                                                              theme.palette
                                                                  .primary.main,
                                                              0.08
                                                          )
                                                        : "#ffffff",
                                                    color: isBusy
                                                        ? "#9ca3af"
                                                        : isSelected
                                                        ? theme.palette.primary
                                                              .dark
                                                        : "#111827",
                                                    fontWeight: isSelected
                                                        ? 700
                                                        : 600,
                                                    "&:hover": {
                                                        borderColor:
                                                            theme.palette
                                                                .primary.main,
                                                        backgroundColor: alpha(
                                                            theme.palette
                                                                .primary.main,
                                                            isSelected
                                                                ? 0.12
                                                                : 0.04
                                                        ),
                                                    },
                                                }}
                                            >
                                                {register.registerName}
                                                {isBusy
                                                    ? ` · ${registerBusySuffix}`
                                                    : ""}
                                            </Button>
                                        )
                                    })}
                                </Box>
                            ) : null}
                        </Box>
                    </Box>
                ) : null}

                {canForceTimeout && selectedRegister?.isBusy ? (
                    <Alert severity="info" sx={{ mt: 1.5, mb: 0.5 }}>
                        {t("cashRegisterSelection.timeoutSection.hint")}
                    </Alert>
                ) : null}

                <Box
                    sx={{
                        mt: 2,
                        p: 1.75,
                        borderRadius: 2,
                        border: "1px solid #e5e7eb",
                        backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.03
                        ),
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "0.85rem",
                            color: "#6b7280",
                            fontWeight: 700,
                            mb: 1,
                        }}
                    >
                        {t("cashRegisterSelection.modeSection.title")}
                    </Typography>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                            gap: 1.25,
                        }}
                    >
                        <Button
                            fullWidth
                            variant="outlined"
                            color="inherit"
                            disabled={
                                !canStartSession || openingReferenceLoading
                            }
                            onClick={handleOpenCountDialog}
                            sx={{
                                py: 1.25,
                                fontWeight: 700,
                                borderColor: alpha(
                                    theme.palette.primary.main,
                                    0.4
                                ),
                                backgroundColor: "#ffffff",
                                "&:hover": {
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        0.06
                                    ),
                                },
                            }}
                        >
                            {t("cashRegisterSelection.modeSection.countButton")}
                        </Button>
                        <Button
                            fullWidth
                            variant="contained"
                            disabled={
                                !canStartSession || openingReferenceLoading
                            }
                            onClick={handleOpenWithPreviousFloat}
                            sx={{ py: 1.25, fontWeight: 800 }}
                        >
                            {t("cashRegisterSelection.previousFloat.button", {
                                amount: previousFundLabel,
                            })}
                        </Button>
                    </Box>
                    {canForceTimeout && selectedRegister?.isBusy ? (
                        <Button
                            fullWidth
                            variant="outlined"
                            color="warning"
                            disabled={!canTimeoutSelectedRegister}
                            onClick={handleOpenTimeoutDialog}
                            sx={{ mt: 1.25, py: 1.1, fontWeight: 700 }}
                        >
                            {timingOutRegisterId ===
                            selectedRegister.registerId ? (
                                <CircularProgress size={18} />
                            ) : (
                                t("cashRegisterSelection.timeoutSection.button")
                            )}
                        </Button>
                    ) : null}
                    {!selectedRegister ? (
                        <Typography
                            sx={{
                                mt: 1,
                                fontSize: "0.78rem",
                                color: "#6b7280",
                            }}
                        >
                            {t(
                                "cashRegisterSelection.modeSection.selectRegisterHint"
                            )}
                        </Typography>
                    ) : null}
                </Box>
            </Paper>

            <CashFundCountDialog
                open={showCountDialog}
                theoreticalMinor={openingReferenceMinor}
                chequeTheoreticalMinor={chequeOpeningReferenceMinor}
                chequeTheoreticalCount={chequeOpeningReferenceCount}
                loading={submitting}
                onCancel={() => setShowCountDialog(false)}
                onConfirm={handleConfirmCount}
            />

            <Dialog
                open={showTimeoutDialog}
                onClose={submitting ? undefined : handleCloseTimeoutDialog}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2.5,
                        border: `1px solid ${alpha(
                            theme.palette.warning.main,
                            0.25
                        )}`,
                        boxShadow: "0 14px 30px rgba(15, 23, 42, 0.16)",
                        overflow: "hidden",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        fontWeight: 800,
                        color: theme.palette.warning.dark,
                        borderBottom: "1px solid #f1f5f9",
                    }}
                >
                    {t("cashRegisterSelection.timeoutSection.button")}
                </DialogTitle>
                <DialogContent sx={{ pt: 2.25 }}>
                    <Alert severity="warning" sx={{ mb: 1.25 }}>
                        {t("cashRegisterSelection.timeoutSection.hint")}
                    </Alert>
                    <Typography color="text.secondary">
                        {t("cashRegisterSelection.timeoutSection.confirm", {
                            register: timeoutDialogRegister?.registerName || "",
                        })}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.25, gap: 1 }}>
                    <Button
                        onClick={handleCloseTimeoutDialog}
                        disabled={submitting}
                        variant="outlined"
                        color="inherit"
                        sx={{ fontWeight: 700 }}
                    >
                        {t("common.cancel")}
                    </Button>
                    <Button
                        onClick={handleTimeoutSelectedRegister}
                        disabled={!canConfirmTimeoutDialog}
                        variant="contained"
                        color="warning"
                        sx={{ fontWeight: 800 }}
                    >
                        {submitting ? (
                            <CircularProgress size={18} color="inherit" />
                        ) : (
                            t("common.confirm")
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}

export default CashRegisterSelectionPage
